// Ring buffer borné générique (dernières entrées, les plus anciennes évincées).
// Pur/testable : la capture (fetch/XHR/console) et le flush rrweb vivent dans
// rrweb-recorder.ts.

export type NetworkEntry = {
  method: string;
  url: string;
  /** Code HTTP. ABSENT quand le navigateur ne l'expose pas — une ressource
   *  cross-origin sans `Timing-Allow-Origin` observée via Resource Timing. On
   *  préfère l'absence à un `0` trompeur : `0` garde son sens strict, « la
   *  requête n'a pas abouti ». Une donnée de diagnostic fausse est pire
   *  qu'absente. */
  status?: number;
  durationMs: number;
  responseSize?: number;
  contentType?: string;
  startedAt: number;
  failed?: boolean;
  /** Origine rapportée par Resource Timing (`img`, `script`, `css`, `link`…).
   *  Absent sur les entrées issues du wrapper `fetch`/XHR. */
  initiatorType?: string;
  /** Identifiant de corrélation renvoyé par l'API, si exposé par CORS. */
  requestId?: string;
  /** Trace HTTP renvoyée par le serveur, tronquée et non interprétée côté SDK. */
  traceparent?: string;
  /** Run de test opaque, utile quand le blob est extrait hors du ticket. */
  runId?: string;
};

export type Ring<T> = {
  push: (entry: T) => void;
  /** Copie NON destructive des entrées courantes (les plus anciennes évincées
   *  au-delà de `max`). Ne vide PAS le ring : les rings de diagnostic sont
   *  permanents et un flush de session ne doit jamais en effacer l'historique. */
  snapshot: () => T[];
};

export type NetworkRing = Ring<NetworkEntry>;

export function createRing<T>(max: number): Ring<T> {
  const buf: T[] = [];
  return {
    push(entry) {
      buf.push(entry);
      if (buf.length > max) buf.shift();
    },
    snapshot() {
      return buf.slice();
    },
  };
}

export function createNetworkRing(max: number): NetworkRing {
  return createRing<NetworkEntry>(max);
}
